import { withDependencies, named } from '@wix/thunderbolt-ioc'
import {
	SiteFeatureConfigSymbol,
	PageFeatureConfigSymbol,
	IPropsStore,
	Props,
	StructureAPI,
	IStructureAPI,
	IPageDidMountHandler,
	IPageDidUnmountHandler,
} from '@wix/thunderbolt-symbols'
import type { MobileActionsMenuSiteConfig, MobileActionsMenuPageConfig } from './types'
import { name, MOBILE_ACTIONS_MENU_ID, MOBILE_ACTIONS_MENU_COMP_TYPE } from './symbols'
import { IScrollBlockedListener, ISiteScrollBlocker, SiteScrollBlockerSymbol } from 'feature-site-scroll-blocker'
import { IRoutingLinkUtilsAPI, RoutingLinkUtilsAPISymbol } from 'feature-router'
import type { MenuItemProps } from '@wix/thunderbolt-becky-types'

const socialLinkNameMap: Record<string, string> = {
	facebook: 'Facebook',
	twitter: 'Twitter',
	pinterest: 'Pinterest',
	google_plus: 'Google+',
	tumblr: 'Tumblr',
	blogger: 'Blogger',
	linkedin: 'LinkedIn',
	youtube: 'YouTube',
	vimeo: 'Vimeo',
	flickr: 'Flickr',
	'': '',
}

const modifyContactInfo = (
	contactInfo: MobileActionsMenuSiteConfig['contactInfo']
): MobileActionsMenuSiteConfig['contactInfo'] => {
	const { phone, email, address } = contactInfo
	return {
		...contactInfo,
		phone: phone ? `tel:${phone}` : phone,
		email: email ? `mailto:${email}` : email,
		address: address ? `http://maps.apple.com/?q=${address}` : address,
	}
}

export const MobileActionsMenuPage = withDependencies(
	[
		named(SiteFeatureConfigSymbol, name),
		named(PageFeatureConfigSymbol, name),
		Props,
		SiteScrollBlockerSymbol,
		StructureAPI,
		RoutingLinkUtilsAPISymbol,
	],
	(
		siteFeatureConfig: MobileActionsMenuSiteConfig,
		{ pageItems }: MobileActionsMenuPageConfig,
		propsStore: IPropsStore,
		siteScrollBlocker: ISiteScrollBlocker,
		structureAPI: IStructureAPI,
		routingLinkUtils: IRoutingLinkUtilsAPI
	): IPageDidMountHandler & IPageDidUnmountHandler => {
		let scrollBlockedListenerId: unknown
		const onScrollBlocked: IScrollBlockedListener = {
			handleBlockedBy(blockedCompId) {
				if (blockedCompId !== MOBILE_ACTIONS_MENU_ID) {
					propsStore.update({ [MOBILE_ACTIONS_MENU_ID]: { shouldHide: true } })
				}
			},
			handleUnblockedBy(unblockedCompId) {
				if (unblockedCompId !== MOBILE_ACTIONS_MENU_ID) {
					propsStore.update({ [MOBILE_ACTIONS_MENU_ID]: { shouldHide: false } })
				}
			},
		}

		return {
			async pageDidMount() {
				scrollBlockedListenerId = siteScrollBlocker.registerScrollBlockedListener(onScrollBlocked)
				const { theme, socialLinks, contactInfo, configuration } = siteFeatureConfig
				const { routes, pageId, mainPageId } = routingLinkUtils.getLinkUtilsRoutingInfo()

				propsStore.update({
					[MOBILE_ACTIONS_MENU_ID]: {
						theme,
						contactInfo: modifyContactInfo(contactInfo),
						configuration,
						currentUrlPageId: pageId,
						socialLinks: socialLinks.map((link) => ({
							label: socialLinkNameMap[link.id] || link.id,
							url: link.url,
						})),
						pageItems: pageItems.map((item: MenuItemProps) => {
							const href = item.link?.href
							if (!href) {
								return item
							}

							if (href === './') {
								return { ...item, id: mainPageId }
							}
							if (routes[href]) {
								return { ...item, id: routes[href].pageId }
							}
							return item
						}),
					},
				})
				if (!structureAPI.isComponentInDynamicStructure(MOBILE_ACTIONS_MENU_ID)) {
					await structureAPI.addComponentToDynamicStructure(MOBILE_ACTIONS_MENU_ID, {
						componentType: MOBILE_ACTIONS_MENU_COMP_TYPE,
						components: [],
					})
				}
			},
			pageDidUnmount() {
				siteScrollBlocker.unRegisterScrollBlockedListener(scrollBlockedListenerId)
			},
		}
	}
)
